import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  Box,
  Chip,
} from "@mui/material";
import {
  collection,
  query,
  where,
  onSnapshot,
  doc,
  getDoc,
} from "firebase/firestore";
import { db, auth } from "../../firebase"; // Import auth
import { format } from "date-fns";
import { formatCurrency } from "../../utils/currency";

export default function ProjectDetails({ open, onClose, projectId }) {
  const [projectName, setProjectName] = useState("");
  const [invoices, setInvoices] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [payroll, setPayroll] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!projectId || !open) return;

    setLoading(true);

    const fetchProject = async () => {
      const projectDoc = await getDoc(doc(db, "projects", projectId));
      if (projectDoc.exists()) {
        setProjectName(projectDoc.data().name || "");
      } else {
        setProjectName("");
      }
    };
    fetchProject();

    const user = auth.currentUser;
    if (!user) return;

    const invoicesQuery = query(
      collection(db, "invoices"),
      where("projectId", "==", projectId),
      where("userId", "==", user.uid)
    );
    const receiptsQuery = query(
      collection(db, "receipts"),
      where("projectId", "==", projectId),
      where("uid", "==", user.uid)
    );
    const payrollQuery = query(
      collection(db, "payroll"),
      where("projectId", "==", projectId),
      where("uid", "==", user.uid)
    );

    const unsubscribeInvoices = onSnapshot(invoicesQuery, (snapshot) => {
      const data = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setInvoices(data);
      setLoading(false);
    });

    const unsubscribeReceipts = onSnapshot(receiptsQuery, (snapshot) => {
      const data = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setReceipts(data);
      setLoading(false);
    });

    const unsubscribePayroll = onSnapshot(payrollQuery, (snapshot) => {
      const data = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setPayroll(data);
      setLoading(false);
    });

    return () => {
      unsubscribeInvoices();
      unsubscribeReceipts();
      unsubscribePayroll();
    };
  }, [projectId, open]);

  const totalInvoiced = invoices.reduce((sum, inv) => {
    if (inv.status === "paid") {
      return sum + (parseFloat(inv.totalAmount) || 0);
    }
    return sum;
  }, 0);
  const totalReceipts = receipts.reduce(
    (sum, rec) => sum + (parseFloat(rec.amount) || 0),
    0
  );
  const totalPayroll = payroll.reduce(
    (sum, p) => sum + (parseFloat(p.amount) || 0),
    0
  );
  const netProfit = totalInvoiced - (totalReceipts + totalPayroll);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Project Details {projectName ? `(${projectName})` : ""}
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box textAlign="center" p={4}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Typography variant="h6" gutterBottom>
              Summary
            </Typography>
            <Typography>
              Total Invoiced (Paid): {formatCurrency(totalInvoiced)}
            </Typography>
            <Typography>
              Total Receipts: {formatCurrency(totalReceipts)}
            </Typography>
            <Typography>
              Total Payroll: {formatCurrency(totalPayroll)}
            </Typography>
            <Typography
              variant="subtitle1"
              fontWeight="bold"
              mt={1}
              mb={3}
              sx={{
                color:
                  netProfit > 0
                    ? "success.main"
                    : netProfit < 0
                    ? "error.main"
                    : "text.secondary",
              }}
            >
              Net Profit: {formatCurrency(netProfit)}{" "}
              <Typography component="span" variant="caption" color="text.secondary">
                (Paid Invoices Only)
              </Typography>
            </Typography>

            <Typography variant="h6" gutterBottom>
              Invoices
            </Typography>
            {invoices.length === 0 ? (
              <Typography>No invoices linked to this project.</Typography>
            ) : (
              <Table size="small" sx={{ mb: 3 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Invoice #</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Total (€)</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invoices.map((inv) => (
                    <TableRow key={inv.id}>
                      <TableCell>{inv.invoiceNumber || "—"}</TableCell>
                      <TableCell>
                        {inv.invoiceDate
                          ? format(inv.createdAt.toDate(), "d MMMM, yyyy")
                          : "—"}
                      </TableCell>
                      <TableCell>
                        {formatCurrency(inv.totalAmount || 0)}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={inv.status || "unpaid"}
                          color={inv.status === "paid" ? "success" : "error"}
                          variant="outlined"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}

            <Typography variant="h6" gutterBottom>
              Receipts
            </Typography>
            {receipts.length === 0 ? (
              <Typography>No receipts linked to this project.</Typography>
            ) : (
              <Table size="small" sx={{ mb: 3 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>File Name</TableCell>
                    <TableCell>Date Uploaded</TableCell>
                    <TableCell>Amount (€)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {receipts.map((rec) => (
                    <TableRow key={rec.id}>
                      <TableCell>{rec.fileName || "—"}</TableCell>
                      <TableCell>
                        {rec.uploadedAt
                          ? format(rec.uploadedAt.toDate(), "d MMMM, yyyy")
                          : "—"}
                      </TableCell>
                      <TableCell>{formatCurrency(rec.amount || 0)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}

            <Typography variant="h6" gutterBottom>
              Payroll
            </Typography>
            {payroll.length === 0 ? (
              <Typography>
                No payroll records linked to this project.
              </Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Employee</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Amount (€)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {payroll.map((p) => (
                    <TableRow key={p.id}>
                      <TableCell>{p.name || "—"}</TableCell>
                      <TableCell>
                        {p.createdAt
                          ? format(p.createdAt.toDate(), "d MMMM, yyyy")
                          : "—"}
                      </TableCell>
                      <TableCell>{formatCurrency(p.amount || 0)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
